import {getTestProps} from '@benchling/js-test-utils';
import {Badge, Button as MantineButton, createStyles, Flex, Group, Text, Tooltip} from '@mantine/core';
import React, {useCallback, useState} from 'react';

import BenchlingConfig from 'BenchlingConfig';
import {formatCanvasVersionLabel} from 'canvas-core/canvasVersionUtil';
import {saveChatCanvasToFolder} from 'canvas-core/chat/saveChatCanvasToFolder';
import {
  ChatCanvasVersionSummary,
  useChatCanvasVersionState,
} from 'canvas-core/chat/useChatCanvasVersionState';
import CanvasPreviewFrame from 'canvas-core/preview/CanvasPreviewFrame';
import useCanvasExpandedPreviewUrl from 'canvas-core/preview/useCanvasExpandedPreviewUrl';
import {ArtifactComponentProps, ArtifactHeaderSlots} from 'llm/chat-library/ArtifactTypes';
import type {CanvasArtifactData} from 'llm/chat-library/useCanvasArtifact';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    height: '100%',
  },
  header: {
    alignItems: 'center',
    borderBottom: `1px solid ${theme.colors.gray[2]}`,
    display: 'flex',
    flexShrink: 0,
    justifyContent: 'space-between',
    padding: `${theme.spacing.sm} ${theme.spacing.md}`,
  },
  headerLeft: {
    alignItems: 'center',
    display: 'flex',
    gap: theme.spacing.xs,
    minWidth: 0,
  },
  iconTile: {
    alignItems: 'center',
    backgroundColor: theme.colors.blue[0],
    borderRadius: theme.radius.sm,
    color: theme.colors.blue[8],
    display: 'flex',
    flexShrink: 0,
    height: 32,
    justifyContent: 'center',
    width: 32,
  },
  title: {
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: 1.3,
  },
  headerActions: {
    alignItems: 'center',
    display: 'flex',
    flexShrink: 0,
    gap: theme.spacing.xs,
  },
  preview: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    minHeight: 0,
    padding: theme.spacing.sm,
  },
}));

function ChatCanvasArtifactContent({
  chatSessionId,
  canvasVersionId,
  canvasVersion,
  onClose,
  renderHeader,
}: {
  chatSessionId: string;
  canvasVersionId: string;
  canvasVersion: ChatCanvasVersionSummary;
  onClose?: () => void;
  renderHeader?: (slots: ArtifactHeaderSlots) => React.ReactNode;
}): JSX.Element {
  const {classes} = useStyles();
  const [refreshToken, setRefreshToken] = useState(0);
  const {signedUrl, isLoading, errorMessage, sdkVersion} = useCanvasExpandedPreviewUrl(
    {chatSessionId, canvasVersionId},
    refreshToken
  );

  const handleRefresh = useCallback(() => {
    setRefreshToken((token) => token + 1);
  }, []);

  const handleSave = useCallback(() => {
    saveChatCanvasToFolder({
      chatSessionId,
      name: canvasVersion.name,
      canvasVersionId,
    });
  }, [canvasVersion.name, canvasVersionId, chatSessionId]);

  const versionBadge = (
    <Badge color="gray" size="xs" variant="light">
      {formatCanvasVersionLabel(canvasVersion.versionIndex)}
    </Badge>
  );

  const actions = (
    <>
      <Tooltip label={isLoading ? 'Refreshing canvas...' : 'Refresh canvas'}>
        <span>
          <Button
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            size={ButtonSize.SMALL}
            onClick={handleRefresh}
            disabled={isLoading}
            icon="refresh"
            aria-label="Refresh canvas"
            {...getTestProps({component: 'ChatCanvasArtifactView', element: 'refreshButton'})}
          />
        </span>
      </Tooltip>
      {BenchlingConfig.ENABLE_CANVAS_SAVE_TO_FOLDER && (
        <MantineButton
          onClick={handleSave}
          size="xs"
          variant="primary"
          {...getTestProps({component: 'ChatCanvasArtifactView', element: 'saveButton'})}
        >
          Save
        </MantineButton>
      )}
    </>
  );

  return (
    <div
      className={classes.container}
      {...getTestProps({component: 'ChatCanvasArtifactView', element: 'root'})}
    >
      {renderHeader != null ? (
        renderHeader({badges: versionBadge, actions})
      ) : (
        <div
          className={classes.header}
          {...getTestProps({component: 'ChatCanvasArtifactView', element: 'header'})}
        >
          <Group align="center" className={classes.headerLeft} noWrap={true} spacing="sm">
            <div className={classes.iconTile}>
              <BenchlingIcon icon="canvas_definition" />
            </div>
            <Group align="center" noWrap={true} spacing="xs">
              <Text className={classes.title}>{canvasVersion.name}</Text>
              {versionBadge}
            </Group>
          </Group>
          <Group className={classes.headerActions} noWrap={true} spacing="xs">
            {actions}
            {onClose != null && (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                size={ButtonSize.SMALL}
                icon="close"
                onClick={onClose}
                aria-label="Close canvas preview"
                {...getTestProps({component: 'ChatCanvasArtifactView', element: 'closeButton'})}
              />
            )}
          </Group>
        </div>
      )}
      <div
        className={classes.preview}
        {...getTestProps({component: 'ChatCanvasArtifactView', element: 'preview'})}
      >
        {isLoading ? (
          <CenteredSpinner />
        ) : errorMessage != null ? (
          <InlineBanner bannerType={InlineBannerType.ERROR} text={errorMessage} />
        ) : signedUrl != null ? (
          <CanvasPreviewFrame signedUrl={signedUrl} sdkVersion={sdkVersion} />
        ) : (
          <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas preview." />
        )}
      </div>
    </div>
  );
}

export function ChatCanvasArtifactView({
  data,
  onClose,
  renderHeader,
}: ArtifactComponentProps<CanvasArtifactData>): JSX.Element {
  // eslint-disable-next-line benchlint/no-multiple-use-styles -- pre-existing multiple createStyles hook calls in this file
  const {classes} = useStyles();
  const {canvasDefinitionId, canvasVersionId, chatSessionId} = data;
  const queryResult = useChatCanvasVersionState(chatSessionId, canvasDefinitionId, canvasVersionId);

  // Before the canvas version loads there are no badges or actions yet, but a layout-provided
  // header still renders so its title and navigation stay in place.
  const withLayoutHeader = (body: JSX.Element): JSX.Element =>
    renderHeader != null ? (
      <div className={classes.container}>
        {renderHeader({})}
        {body}
      </div>
    ) : (
      body
    );

  if (queryResult.type === 'skipped') {
    return withLayoutHeader(
      <InlineBanner
        bannerType={InlineBannerType.ERROR}
        text="Canvas is not available for your organization."
      />
    );
  }

  if (queryResult.type === 'pending') {
    return withLayoutHeader(
      <Flex align="center" className={classes.container} justify="center">
        <CenteredSpinner />
      </Flex>
    );
  }

  if (queryResult.type === 'error') {
    return withLayoutHeader(
      <InlineBanner bannerType={InlineBannerType.ERROR} text="Unable to load canvas details." />
    );
  }

  return (
    <ChatCanvasArtifactContent
      chatSessionId={chatSessionId}
      canvasVersionId={canvasVersionId}
      canvasVersion={queryResult.canvasVersion}
      onClose={onClose}
      renderHeader={renderHeader}
    />
  );
}
